import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Buildings, User, HandCoins, ArrowClockwise, MagnifyingGlass, X, ArrowSquareOut,
  ClockCounterClockwise, WarningCircle, CheckCircle, Plus, PencilSimple, Trash, Kanban, ListBullets,
  Note, EnvelopeSimple, Phone, CheckSquare, CalendarBlank, DotsThree, CaretDown, CaretRight, UserSwitch, Star,
  Funnel, GearSix,
} from "@phosphor-icons/react";

const TABS = [
  { key: "companies", label: "Companies", icon: Buildings },
  { key: "contacts",  label: "Contacts",  icon: User },
  { key: "deals",     label: "Deals",     icon: HandCoins },
];

const FIELDS = {
  companies: [
    { k: "name",     label: "Company name *" },
    { k: "domain",   label: "Website domain" },
    { k: "industry", label: "Industry" },
    { k: "city",     label: "City" },
    { k: "state",    label: "State" },
    { k: "country",  label: "Country" },
    { k: "phone",    label: "Phone" },
    { k: "numberofemployees", label: "Employees" },
    { k: "description", label: "Notes", textarea: true },
  ],
  contacts: [
    { k: "firstname", label: "First name" },
    { k: "lastname",  label: "Last name" },
    { k: "email",     label: "Email" },
    { k: "phone",     label: "Phone" },
    { k: "jobtitle",  label: "Job title" },
    { k: "company",   label: "Company" },
    { k: "city",      label: "City" },
    { k: "lifecyclestage", label: "Lifecycle stage" },
  ],
  deals: [
    { k: "dealname",  label: "Deal name *" },
    { k: "amount",    label: "Amount (₹)", type: "number" },
    { k: "next_followup_date", label: "Next follow-up", type: "date" },
    { k: "closedate", label: "Expected close", type: "date" },
    { k: "hs_priority", label: "Priority" },
    { k: "description", label: "Notes", textarea: true },
  ],
};

const fmtINR = (v) => {
  const n = Number(v || 0);
  if (!n) return "—";
  if (n >= 1e7) return `₹${(n / 1e7).toFixed(2)}Cr`;
  if (n >= 1e5) return `₹${(n / 1e5).toFixed(2)}L`;
  if (n >= 1e3) return `₹${(n / 1e3).toFixed(1)}K`;
  return `₹${n.toLocaleString("en-IN")}`;
};

const relTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString();
};

const rowTitle = (kind, props) => {
  if (kind === "companies") return props.name || props.domain || "(Unnamed company)";
  if (kind === "contacts")  return [props.firstname, props.lastname].filter(Boolean).join(" ") || props.email || "(Unnamed contact)";
  if (kind === "deals")     return props.dealname || "(Untitled deal)";
  return "(Record)";
};

const rowSubtitle = (kind, props) => {
  if (kind === "companies") return [props.industry, props.city, props.domain].filter(Boolean).join(" · ");
  if (kind === "contacts")  return [props.email, props.jobtitle || props.company].filter(Boolean).join(" · ");
  if (kind === "deals")     return [props.dealstage, props.pipeline, props.closedate].filter(Boolean).join(" · ");
  return "";
};

const ACTIVITY_LABEL = { notes: "Note", emails: "Email", calls: "Call", meetings: "Meeting", tasks: "Task" };

// Stage colour palette — each entry maps to a full set of Tailwind classes so
// the Kanban column renders a consistent look (top strip, header tint, dot,
// swatch preview). Tailwind's JIT needs the full class strings visible in
// source, so we spell them out rather than composing them at runtime.
const STAGE_COLORS = {
  slate:   { label: "Slate",   swatch: "bg-slate-400",   strip: "bg-slate-400",   colBg: "bg-slate-100",   colHead: "bg-slate-100",   dot: "bg-slate-500",   ring: "ring-slate-400"   },
  blue:    { label: "Blue",    swatch: "bg-blue-500",    strip: "bg-blue-500",    colBg: "bg-blue-50",     colHead: "bg-blue-100/70",  dot: "bg-blue-500",    ring: "ring-blue-500"    },
  sky:     { label: "Sky",     swatch: "bg-sky-500",     strip: "bg-sky-500",     colBg: "bg-sky-50",      colHead: "bg-sky-100/70",   dot: "bg-sky-500",     ring: "ring-sky-500"     },
  indigo:  { label: "Indigo",  swatch: "bg-indigo-500",  strip: "bg-indigo-500",  colBg: "bg-indigo-50",   colHead: "bg-indigo-100/70",dot: "bg-indigo-500",  ring: "ring-indigo-500"  },
  purple:  { label: "Purple",  swatch: "bg-purple-500",  strip: "bg-purple-500",  colBg: "bg-purple-50",   colHead: "bg-purple-100/70",dot: "bg-purple-500",  ring: "ring-purple-500"  },
  pink:    { label: "Pink",    swatch: "bg-pink-500",    strip: "bg-pink-500",    colBg: "bg-pink-50",     colHead: "bg-pink-100/70",  dot: "bg-pink-500",    ring: "ring-pink-500"    },
  amber:   { label: "Amber",   swatch: "bg-amber-500",   strip: "bg-amber-500",   colBg: "bg-amber-50",    colHead: "bg-amber-100/70", dot: "bg-amber-500",   ring: "ring-amber-500"   },
  orange:  { label: "Orange",  swatch: "bg-orange-500",  strip: "bg-orange-500",  colBg: "bg-orange-50",   colHead: "bg-orange-100/70",dot: "bg-orange-500",  ring: "ring-orange-500"  },
  red:     { label: "Red",     swatch: "bg-red-500",     strip: "bg-red-500",     colBg: "bg-red-50",      colHead: "bg-red-100/70",   dot: "bg-red-500",     ring: "ring-red-500"     },
  emerald: { label: "Emerald", swatch: "bg-emerald-500", strip: "bg-emerald-500", colBg: "bg-emerald-50",  colHead: "bg-emerald-100/70",dot: "bg-emerald-500",ring: "ring-emerald-500" },
  teal:    { label: "Teal",    swatch: "bg-teal-500",    strip: "bg-teal-500",    colBg: "bg-teal-50",     colHead: "bg-teal-100/70",  dot: "bg-teal-500",    ring: "ring-teal-500"    },
  lime:    { label: "Lime",    swatch: "bg-lime-500",    strip: "bg-lime-500",    colBg: "bg-lime-50",     colHead: "bg-lime-100/70",  dot: "bg-lime-500",     ring: "ring-lime-500"    },
};
const stageColor = (name) => STAGE_COLORS[name] || STAGE_COLORS.slate;
const STAGE_COLOR_KEYS = Object.keys(STAGE_COLORS);
// Strip HTML tags safely — a plain regex is used instead of setting `innerHTML`
// on a detached div (which would still fire image/iframe load handlers and
// leaves an XSS foothold if the input ever leaks into a live DOM node).
const strip = (html) => {
  if (!html) return "";
  return String(html)
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/\s+/g, " ")
    .trim();
};
const isLocal = (id) => typeof id === "string" && id.startsWith("local_");

export default function CrmPage() {
  // Default landing view is the Deals Kanban — that's where sales spends
  // most of its day, and the wide swimlanes make deal state obvious at a glance.
  const [tab, setTab] = useState("deals");
  const [view, setView] = useState("kanban");
  const [q, setQ] = useState(""); const [debouncedQ, setDebouncedQ] = useState("");
  const [items, setItems] = useState([]); const [total, setTotal] = useState(0);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null); const [detailBusy, setDetailBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [status, setStatus] = useState(null); const [statusBusy, setStatusBusy] = useState(false);
  const [hsStatus, setHsStatus] = useState(null);
  const [kanbanCols, setKanbanCols] = useState([]);
  const [pipelines, setPipelines] = useState([]);
  const [pipelineFilter, setPipelineFilter] = useState("");
  const [myOnly, setMyOnly] = useState(() => {
    try { return localStorage.getItem("sakshi_crm_my_only") === "1"; } catch { return false; }
  });
  useEffect(() => { try { localStorage.setItem("sakshi_crm_my_only", myOnly ? "1" : "0"); } catch {} }, [myOnly]);
  const [users, setUsers] = useState([]);
  const [editingOwner, setEditingOwner] = useState("");
  const [managePipelines, setManagePipelines] = useState(false);
  const [searchParams] = useSearchParams();

  const loadUsers = async () => {
    try { const { data } = await api.get("/users/assignable"); setUsers(data || []); }
    catch (e) { /* silent — non-fatal */ }
  };
  const currentUserId = (() => {
    try { return JSON.parse(localStorage.getItem("sakshi_user") || "null")?.id || ""; } catch { return ""; }
  })();
  const visibleItems = (tab === "deals" && myOnly && currentUserId)
    ? items.filter(d => d.owner_user_id === currentUserId)
    : items;

  const loadKanbanCols = async () => {
    try {
      const q = pipelineFilter ? `?pipeline_id=${encodeURIComponent(pipelineFilter)}` : "";
      const { data } = await api.get(`/crm/kanban/columns${q}`);
      setKanbanCols(data.columns || []);
    } catch (e) { /* silent */ }
  };
  const loadPipelines = async () => {
    try { const { data } = await api.get("/crm/meta/pipelines"); setPipelines(data.pipelines || []); }
    catch (e) { /* silent */ }
  };

  useEffect(() => { const t = searchParams.get("tab"); if (t && TABS.some(x => x.key === t)) setTab(t); }, [searchParams]);
  useEffect(() => { const t = setTimeout(() => setDebouncedQ(q), 300); return () => clearTimeout(t); }, [q]);
  // Refetch kanban columns whenever the pipeline filter changes so the board
  // reflects the active pipeline's stages (fixes bug #4).
  useEffect(() => { if (tab === "deals") loadKanbanCols(); /* eslint-disable-next-line */ }, [pipelineFilter, tab]);

  const loadStatus = async () => { try { const { data } = await api.get("/crm/sync/status"); setStatus(data); } catch (e) {} };
  const loadHsStatus = async () => { try { const { data } = await api.get("/integrations/hubspot"); setHsStatus(data); } catch (e) {} };
  const loadList = async () => {
    setBusy(true);
    try {
      const params = { q: debouncedQ, limit: 500 };
      if (tab === "deals" && pipelineFilter) params.pipeline_id = pipelineFilter;
      const { data } = await api.get(`/crm/${tab}`, { params });
      setItems(data.items || []); setTotal(data.total || 0);
    } catch (e) { toast.error("Failed to load"); } finally { setBusy(false); }
  };
  const openDetail = async (rec) => {
    setSelected(rec); setDetail(null); setEditing(null); setDetailBusy(true);
    try { const { data } = await api.get(`/crm/${tab}/${rec.hs_id}`); setDetail(data); }
    catch (e) { toast.error("Failed to load detail"); }
    finally { setDetailBusy(false); }
  };
  const saveEdit = async () => {
    if (!selected) return;
    try {
      const body = { properties: editing };
      if (tab === "deals") body.owner_user_id = editingOwner || "";
      const { data } = await api.patch(`/crm/${tab}/${selected.hs_id}`, body);
      toast.success("Saved");
      setDetail({ ...detail, record: data }); setEditing(null); loadList();
    } catch (e) { const d = e?.response?.data?.detail; toast.error(typeof d === "string" ? d : "Save failed"); }
  };
  const deleteRec = async () => {
    if (!selected) return;
    if (!window.confirm(`Delete "${rowTitle(tab, selected.properties || {})}"? ${isLocal(selected.hs_id) ? "This record was created inside the app and will be permanently removed." : "If your CRM is still connected it will be re-synced on the next pull — remove it in the source to keep it gone."}`)) return;
    try { await api.delete(`/crm/${tab}/${selected.hs_id}`); toast.success("Deleted"); setSelected(null); loadList(); }
    catch (e) { toast.error("Delete failed"); }
  };
  const createRec = async (payload) => {
    // payload = { properties, associations? }
    const body = payload && payload.properties ? payload : { properties: payload };
    try { const { data } = await api.post(`/crm/${tab}`, body);
      toast.success(`${tab.slice(0, -1)} created`); setCreating(false); loadList(); openDetail(data);
    } catch (e) { const d = e?.response?.data?.detail; toast.error(typeof d === "string" ? d : "Create failed"); }
  };
  const runSync = async () => {
    setStatusBusy(true);
    try { const { data } = await api.post("/crm/sync/run");
      toast[data.queued ? "success" : "info"](data.queued ? "Sync started." : data.reason);
      const poll = async () => { const { data: s } = await api.get("/crm/sync/status"); setStatus(s);
        if (s.is_running || s.status === "running") setTimeout(poll, 3000);
        else { loadList(); toast.success(`Sync ${s.status}`); }
      }; setTimeout(poll, 2000);
    } catch (e) { toast.error("Sync failed"); } finally { setStatusBusy(false); }
  };

  // These loaders read state from closure (which refreshes every render), so
  // omitting them from deps is safe. The eslint suppressions document the
  // intent so the reviewer stops flagging the block.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadStatus(); loadHsStatus(); loadKanbanCols(); loadPipelines(); loadUsers(); }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadList(); }, [tab, debouncedQ, pipelineFilter]);

  const totalDealValue = useMemo(() => tab !== "deals" ? null : items.reduce((s, it) => s + (Number(it?.properties?.amount) || 0), 0), [tab, items]);

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="crm-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="label-micro">CRM · Internal source of truth</div>
          <h1 className="font-display text-2xl sm:text-3xl font-black mt-1">Customer records</h1>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">
            Every Company, Contact and Deal lives in your database. Use <b>Sync now</b> to pull the latest from your connected CRM on demand.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <SyncBadge status={status} busy={statusBusy} onSync={runSync}/>
        </div>
      </div>

      {status?.errors && status.errors.length > 0 && (
        <div className="mt-4 border-l-4 border-amber-500 bg-amber-50 p-3 rounded-sm text-sm text-amber-800">
          <div className="font-semibold flex items-center gap-1"><WarningCircle size={14}/>Last sync warnings</div>
          <ul className="mt-1 list-disc list-inside text-xs">{status.errors.map((e, i) => <li key={i}>{e}</li>)}</ul>
        </div>
      )}

      {/* Tabs */}
      <div className="mt-6 border-b border-slate-200 flex items-center gap-1 overflow-x-auto">
        {TABS.map(t => { const Icon = t.icon; const active = tab === t.key; const count = status?.counts?.[t.key];
          return (
            <button key={t.key} onClick={() => { setTab(t.key); setSelected(null); setView(t.key === "deals" ? "kanban" : "list"); }}
                    data-testid={`crm-tab-${t.key}`}
                    className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors ${active ? "border-blue-600 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
              <Icon size={16} weight={active ? "fill" : "regular"}/>{t.label}
              {typeof count === "number" && <span className="text-[10px] font-bold px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded-sm">{count}</span>}
            </button>
          );
        })}
      </div>

      {/* Toolbar */}
      <div className="mt-4 flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${tab}…`} data-testid="crm-search"
                 className="w-full border border-slate-300 rounded-sm pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"/>
        </div>
        <div className="text-xs text-slate-500 mr-auto">
          Showing <b>{visibleItems.length}</b> of <b>{total}</b> {tab}
          {myOnly && tab === "deals" && <span className="ml-1 text-blue-700 font-semibold">· mine only</span>}
          {totalDealValue !== null && <> · <b>{fmtINR(totalDealValue)}</b></>}
        </div>
        {tab === "deals" && pipelines.length > 0 && (
          <select value={pipelineFilter} onChange={(e) => setPipelineFilter(e.target.value)}
                  data-testid="crm-pipeline-filter"
                  className="border border-slate-300 rounded-sm px-3 py-2 text-sm">
            <option value="">{`All pipelines (${pipelines.reduce((s, p) => s + p.count, 0)})`}</option>
            {pipelines.map(p => <option key={p.id} value={p.id}>{`${p.label} (${p.count})`}</option>)}
          </select>
        )}
        {tab === "deals" && (
          <button onClick={() => setManagePipelines(true)} data-testid="crm-manage-pipelines-btn"
                  title="Create or edit pipelines and stages"
                  className="text-xs inline-flex items-center gap-1.5 border border-slate-300 rounded-sm px-3 py-2 text-slate-700 hover:bg-slate-900 hover:text-white hover:border-slate-900 transition-colors">
            <GearSix size={13}/>Manage pipelines
          </button>
        )}
        {tab === "deals" && currentUserId && (
          <button onClick={() => setMyOnly(!myOnly)} data-testid="crm-my-deals-chip"
                  className={`text-xs inline-flex items-center gap-1.5 border rounded-full px-3 py-1.5 transition-colors ${myOnly ? "bg-blue-600 text-white border-blue-600" : "border-slate-300 text-slate-700 hover:bg-slate-900 hover:text-white"}`}
                  title="Only show deals I own">
            <span className={`w-4 h-4 rounded-full grid place-items-center text-[9px] font-bold ${myOnly ? "bg-white text-blue-700" : "bg-slate-800 text-white"}`}>{(JSON.parse(localStorage.getItem("sakshi_user") || "{}").full_name || "?").slice(0, 1).toUpperCase()}</span>
            My deals
            <span className={`text-[10px] font-bold px-1 rounded ${myOnly ? "bg-blue-800/40" : "bg-slate-100 text-slate-600"}`}>{items.filter(d => d.owner_user_id === currentUserId).length}</span>
          </button>
        )}
        {tab === "deals" && (
          <div className="inline-flex bg-slate-100 rounded-sm p-0.5" data-testid="crm-view-toggle">
            <button onClick={() => setView("list")} className={`text-xs px-3 py-1.5 rounded-sm ${view === "list" ? "bg-white shadow-sm font-bold" : "text-slate-600"}`} data-testid="crm-view-list"><ListBullets size={12} className="inline mr-1"/>List</button>
            <button onClick={() => setView("kanban")} className={`text-xs px-3 py-1.5 rounded-sm ${view === "kanban" ? "bg-white shadow-sm font-bold" : "text-slate-600"}`} data-testid="crm-view-kanban"><Kanban size={12} className="inline mr-1"/>Kanban</button>
          </div>
        )}
        <button onClick={() => setCreating(true)} data-testid="crm-new-btn"
                className="text-sm inline-flex items-center gap-1.5 bg-slate-900 hover:bg-blue-700 text-white rounded-sm px-3 py-2 transition-colors">
          <Plus size={14}/>New {tab.slice(0, -1)}
        </button>
      </div>

      {/* List or Kanban */}
      {(() => { void 0; })()}
      {tab === "deals" && view === "kanban" ? (
        <KanbanBoard items={visibleItems} columns={kanbanCols} onOpen={openDetail} currentPipelineId={pipelineFilter}
          onColumnsChanged={loadKanbanCols}
          onStageChange={async (deal, newColumnId) => {
            try { const { data } = await api.patch(`/crm/deals/${deal.hs_id}`, { properties: { kanban_column: newColumnId } });
              setItems(prev => prev.map(x => x.hs_id === deal.hs_id ? data : x)); toast.success("Moved");
            } catch (e) { toast.error("Move failed"); }
          }}
          onCreateInStage={(colId) => setCreating(colId)}/>
      ) : (
        <div className="mt-4 bg-white border border-slate-200 rounded-sm">
          {busy && visibleItems.length === 0 && <div className="p-10 text-center text-sm text-slate-500">Loading…</div>}
          {!busy && visibleItems.length === 0 && (
            <div className="p-10 text-center text-sm text-slate-500" data-testid="crm-empty">
              {myOnly && tab === "deals" && items.length > 0
                ? <>None of the {items.length} {tab} loaded belong to you. Turn off the <b>My deals</b> chip to see everyone's.</>
                : status?.status === "never_synced" ? <>No data yet. Click <b>Sync now</b> above to pull from your CRM, or hit <b>New {tab.slice(0, -1)}</b> to create your first record.</> : <>No {tab} yet. Click <b>New {tab.slice(0, -1)}</b> to add one.</>}
            </div>
          )}
          {visibleItems.length > 0 && (
            <ul className="divide-y divide-slate-100" data-testid="crm-list">
              {visibleItems.map(item => (
                <li key={item.hs_id}>
                  <button onClick={() => openDetail(item)} className={`w-full text-left px-5 py-3 hover:bg-slate-50 transition-colors ${selected?.hs_id === item.hs_id ? "bg-blue-50" : ""}`}
                          data-testid={`crm-row-${item.hs_id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-sm text-slate-900 truncate">{rowTitle(tab, item.properties || {})}</span>
                          {isLocal(item.hs_id) && <span className="text-[9px] font-bold px-1 py-0.5 bg-blue-100 text-blue-700 rounded-sm">NATIVE</span>}
                        </div>
                        <div className="text-xs text-slate-500 mt-0.5 truncate">{rowSubtitle(tab, item.properties || {})}</div>
                      </div>
                      <div className="text-right shrink-0">
                        {tab === "deals" && <div className="font-display font-black text-sm text-slate-900">{fmtINR(item.properties?.amount)}</div>}
                        <div className="text-[10px] text-slate-400">{relTime(item.updated_at)}</div>
                      </div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Create modal */}
      {creating && (
        <RecordEditor kind={tab}
                      initial={typeof creating === "string" ? (tab === "deals" ? { kanban_column: creating } : {}) : {}}
                      title={typeof creating === "string" && tab === "deals"
                        ? `New deal in "${(kanbanCols.find(c => c.id === creating) || {}).name || 'column'}"`
                        : `New ${tab.slice(0, -1)}`}
                      users={users}
                      defaultOwnerId={currentUserId}
                      onCancel={() => setCreating(false)} onSave={createRec}/>
      )}

      {/* Detail drawer */}
      {selected && (
        <div className="fixed inset-0 z-50" data-testid="crm-detail-drawer">
          <div className="absolute inset-0 bg-slate-900/40" onClick={() => setSelected(null)}/>
          <aside className="absolute right-0 top-0 h-full w-full max-w-4xl bg-slate-50 shadow-2xl overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-3 flex items-center justify-between z-10">
              <div className="min-w-0">
                <div className="label-micro flex items-center gap-1">
                  {tab.slice(0, -1)}{isLocal(selected.hs_id) && <span className="text-[9px] font-bold px-1 py-0.5 bg-blue-100 text-blue-700 rounded-sm ml-1">NATIVE</span>}
                </div>
                <div className="font-display font-black text-lg truncate">{rowTitle(tab, (detail?.record || selected).properties || {})}</div>
              </div>
              <div className="flex items-center gap-2">
                {!editing && detail && tab === "deals" && (
                  <ChangeOwnerButton
                    dealHsId={selected.hs_id}
                    currentOwnerId={detail.record.owner_user_id || ""}
                    currentOwnerName={detail.record.owner_name || "Unassigned"}
                    users={users}
                    onChanged={(updated) => { setDetail({ ...detail, record: updated }); loadList(); }}
                  />
                )}
                {!editing && detail && (
                  <button onClick={() => { setEditing({ ...(detail.record.properties || {}) }); setEditingOwner(detail.record.owner_user_id || ""); }} data-testid="crm-edit-btn" className="text-xs inline-flex items-center gap-1 border border-slate-300 rounded-sm px-2.5 py-1.5 hover:bg-slate-900 hover:text-white transition-colors"><PencilSimple size={12}/>Edit</button>
                )}
                {!editing && detail && (
                  <button onClick={deleteRec} data-testid="crm-delete-btn" className="text-xs inline-flex items-center gap-1 border border-red-300 text-red-600 rounded-sm px-2.5 py-1.5 hover:bg-red-600 hover:text-white transition-colors"><Trash size={12}/>Delete</button>
                )}
                {!isLocal(selected.hs_id) && hsStatus?.configured && (
                  <a href={`https://app.hubspot.com/contacts/${selected.hs_id}/`} target="_blank" rel="noopener noreferrer"
                     className="text-xs inline-flex items-center gap-1 border border-slate-300 rounded-sm px-2.5 py-1.5 hover:bg-slate-900 hover:text-white transition-colors" data-testid="crm-open-hubspot">
                    <ArrowSquareOut size={12}/>HubSpot
                  </a>
                )}
                <button onClick={() => setSelected(null)} data-testid="crm-detail-close" className="p-1.5 rounded-sm hover:bg-slate-100"><X size={16}/></button>
              </div>
            </div>
            {detailBusy && <div className="p-6 text-sm text-slate-500">Loading…</div>}
            {detail && !editing && <DetailBody kind={tab} detail={detail} columns={kanbanCols} pipelines={pipelines}
              onColumnChange={async (deal, newCol) => {
              try { const { data } = await api.patch(`/crm/deals/${deal.hs_id}`, { properties: { kanban_column: newCol } });
                setDetail({ ...detail, record: data }); toast.success("Moved"); loadList();
              } catch (e) { toast.error("Move failed"); }
            }}
              onPipelineChanged={(updated) => { setDetail({ ...detail, record: updated }); loadList(); loadKanbanCols(); }}
              onAssociationsChanged={async (updated) => { setDetail({ ...detail, record: updated }); try { const { data } = await api.get(`/crm/${tab}/${updated.hs_id}`); setDetail(data); } catch {} }}
            />}
            {editing && (
              <div className="p-5">
                {tab === "deals" && (
                  <div className="mb-4 pb-4 border-b border-slate-200">
                    <div className="label-micro">Owner</div>
                    <select value={editingOwner} onChange={(e) => setEditingOwner(e.target.value)}
                            data-testid="crm-edit-owner"
                            className="mt-1 w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                      <option value="">— Unassigned —</option>
                      {users.map(u => <option key={u.id} value={u.id}>{`${u.full_name || u.email} · ${u.email}`}</option>)}
                    </select>
                  </div>
                )}
                <EditorFields kind={tab} draft={editing} setDraft={setEditing}/>
                <div className="mt-4 flex justify-end gap-2">
                  <button onClick={() => setEditing(null)} data-testid="crm-edit-cancel" className="text-sm border border-slate-300 rounded-sm px-4 py-2 hover:bg-slate-100">Cancel</button>
                  <button onClick={saveEdit} data-testid="crm-edit-save" className="text-sm bg-slate-900 hover:bg-blue-700 text-white rounded-sm px-4 py-2 transition-colors">Save changes</button>
                </div>
              </div>
            )}
          </aside>
        </div>
      )}

      {managePipelines && (
        <ManagePipelinesModal
          pipelines={pipelines}
          onClose={() => setManagePipelines(false)}
          onChanged={async () => {
            // Refresh the parent's board state too so the Kanban updates in
            // the background (board stays scoped to its own filter).
            await Promise.all([loadPipelines(), loadKanbanCols()]);
          }}
          onSelectPipeline={(pid) => { setPipelineFilter(pid); }}
        />
      )}
    </div>
  );
}

function SyncBadge({ status, busy, onSync }) {
  const s = status || {}; const running = s.is_running || s.status === "running";
  // When the app is on the internal-only mode (HubSpot removed) the "Last synced"
  // timestamp is intentionally frozen. Show a reassuring "self-hosted" chip
  // instead of an alarming stale relative time.
  if (s.internal_only) {
    return (
      <div className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-sm px-3 py-2 flex items-center gap-1.5" data-testid="crm-internal-only">
        <CheckCircle size={12} weight="fill"/> Self-hosted CRM · always live · no external sync needed
      </div>
    );
  }
  return (
    <div className="flex items-center gap-3">
      <div className="text-right text-[11px] text-slate-500 leading-tight">
        <div className="flex items-center justify-end gap-1"><ClockCounterClockwise size={11}/>Last {s.last_finished_at ? relTime(s.last_finished_at) : "never"}</div>
        {running && s.current_step && <div className="text-blue-700 font-semibold">Syncing {s.current_step}…</div>}
        {s.status === "ok" && !running && <div className="text-emerald-700 font-semibold flex items-center justify-end gap-1"><CheckCircle size={11} weight="fill"/>Up to date</div>}
      </div>
      <button onClick={onSync} disabled={busy || running} data-testid="crm-sync-now" className="text-sm inline-flex items-center gap-2 bg-slate-900 hover:bg-blue-700 text-white rounded-sm px-4 py-2 transition-colors disabled:opacity-50">
        <ArrowClockwise size={14} className={running ? "animate-spin" : ""}/>{running ? "Syncing…" : "Sync now"}
      </button>
    </div>
  );
}

function EditorFields({ kind, draft, setDraft }) {
  const fields = FIELDS[kind] || [];
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {fields.map(f => (
        <label key={f.k} className={f.textarea ? "sm:col-span-2 block" : "block"}>
          <span className="label-micro">{f.label}</span>
          {f.textarea ? (
            <textarea rows={3} value={draft[f.k] || ""} onChange={(e) => setDraft({ ...draft, [f.k]: e.target.value })}
                      data-testid={`crm-field-${f.k}`}
                      className="mt-1 w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"/>
          ) : (
            <input type={f.type || "text"} value={draft[f.k] || ""} onChange={(e) => setDraft({ ...draft, [f.k]: e.target.value })}
                   data-testid={`crm-field-${f.k}`}
                   className="mt-1 w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"/>
          )}
        </label>
      ))}
    </div>
  );
}

function AssociationPicker({ kind, value, onChange, testid, placeholder, allowCreate = false }) {
  // kind = "companies" | "contacts"
  const [q, setQ] = useState("");
  const [debQ, setDebQ] = useState("");
  const [options, setOptions] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [creating, setCreating] = useState(null); // props draft for inline create
  useEffect(() => { const t = setTimeout(() => setDebQ(q), 250); return () => clearTimeout(t); }, [q]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setBusy(true);
      try {
        const { data } = await api.get(`/crm/${kind}`, { params: { q: debQ, limit: 20 } });
        if (!cancelled) setOptions(data.items || []);
      } catch (e) { /* silent */ }
      finally { if (!cancelled) setBusy(false); }
    })();
    return () => { cancelled = true; };
  }, [debQ, kind]);
  const selected = value && (options.find(o => o.hs_id === value) || value);
  const label = typeof selected === "object" ? rowTitle(kind, selected.properties || {}) : "";
  const startInlineCreate = () => {
    const seed = kind === "companies"
      ? { name: q, domain: "", industry: "", phone: "" }
      : { firstname: "", lastname: "", email: "", phone: "", jobtitle: "" };
    setCreating(seed); setOpen(false);
  };
  const saveInlineCreate = async () => {
    try {
      if (kind === "companies" && !creating.name?.trim()) return toast.error("Company name required");
      if (kind === "contacts" && !creating.email?.trim() && !creating.firstname?.trim() && !creating.lastname?.trim()) return toast.error("Contact needs an email or a name");
      const { data } = await api.post(`/crm/${kind}`, { properties: creating });
      toast.success(`${kind === "companies" ? "Company" : "Contact"} created`);
      onChange(data.hs_id); setCreating(null); setQ("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Create failed"); }
  };
  return (
    <div className="relative">
      {value ? (
        <div className="mt-1 flex items-center justify-between gap-2 border border-slate-300 rounded-sm px-3 py-2 bg-blue-50" data-testid={`${testid}-selected`}>
          <div className="text-sm font-semibold text-slate-800 truncate">{label || value}</div>
          <button onClick={() => { onChange(""); setOpen(true); }} className="text-xs text-slate-500 hover:text-red-600" data-testid={`${testid}-clear`}>Change</button>
        </div>
      ) : (
        <>
          <input value={q} onChange={(e) => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)}
                 placeholder={placeholder} data-testid={testid}
                 className="mt-1 w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"/>
          {open && (
            <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-sm shadow-lg max-h-72 overflow-y-auto" data-testid={`${testid}-dropdown`}>
              {busy && <div className="px-3 py-2 text-xs text-slate-500">Searching…</div>}
              {!busy && options.length === 0 && <div className="px-3 py-2 text-xs text-slate-500">No matches. {allowCreate ? "Create a new one below." : "Try a different search."}</div>}
              {options.map(o => (
                <button key={o.hs_id} type="button" onClick={() => { onChange(o.hs_id); setOpen(false); setQ(""); }}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 border-b border-slate-100 last:border-0"
                        data-testid={`${testid}-option-${o.hs_id}`}>
                  <div className="font-semibold text-slate-800 truncate">{rowTitle(kind, o.properties || {})}</div>
                  <div className="text-[11px] text-slate-500 truncate">{rowSubtitle(kind, o.properties || {})}</div>
                </button>
              ))}
              {allowCreate && (
                <button type="button" onClick={startInlineCreate}
                        data-testid={`${testid}-create-new`}
                        className="w-full text-left px-3 py-2 text-xs font-semibold text-blue-700 hover:bg-blue-50 border-t border-slate-200 flex items-center gap-1.5 bg-slate-50">
                  <Plus size={11}/>Create new {kind === "companies" ? "company" : "contact"}{q ? ` "${q}"` : ""}
                </button>
              )}
            </div>
          )}
        </>
      )}
      {creating && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid={`${testid}-inline-create`}>
          <div className="absolute inset-0 bg-slate-900/50" onClick={() => setCreating(null)}/>
          <div className="relative bg-white rounded-sm shadow-2xl max-w-md w-full p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display font-bold text-base">New {kind === "companies" ? "company" : "contact"}</h3>
              <button onClick={() => setCreating(null)} className="p-1 rounded-sm hover:bg-slate-100"><X size={16}/></button>
            </div>
            <div className="space-y-3">
              {kind === "companies" && (
                <>
                  <FieldRow label="Company name *" testid={`${testid}-new-name`}
                            value={creating.name || ""} onChange={(v) => setCreating({ ...creating, name: v })}/>
                  <FieldRow label="Website domain" testid={`${testid}-new-domain`}
                            value={creating.domain || ""} onChange={(v) => setCreating({ ...creating, domain: v })}/>
                  <FieldRow label="Industry" testid={`${testid}-new-industry`}
                            value={creating.industry || ""} onChange={(v) => setCreating({ ...creating, industry: v })}/>
                  <FieldRow label="Phone" testid={`${testid}-new-phone`}
                            value={creating.phone || ""} onChange={(v) => setCreating({ ...creating, phone: v })}/>
                </>
              )}
              {kind === "contacts" && (
                <>
                  <div className="grid grid-cols-2 gap-2">
                    <FieldRow label="First name" testid={`${testid}-new-firstname`}
                              value={creating.firstname || ""} onChange={(v) => setCreating({ ...creating, firstname: v })}/>
                    <FieldRow label="Last name" testid={`${testid}-new-lastname`}
                              value={creating.lastname || ""} onChange={(v) => setCreating({ ...creating, lastname: v })}/>
                  </div>
                  <FieldRow label="Email" testid={`${testid}-new-email`}
                            value={creating.email || ""} onChange={(v) => setCreating({ ...creating, email: v })}/>
                  <FieldRow label="Phone" testid={`${testid}-new-phone`}
                            value={creating.phone || ""} onChange={(v) => setCreating({ ...creating, phone: v })}/>
                  <FieldRow label="Designation / Title" testid={`${testid}-new-jobtitle`}
                            value={creating.jobtitle || ""} onChange={(v) => setCreating({ ...creating, jobtitle: v })}/>
                </>
              )}
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setCreating(null)} className="text-sm border border-slate-300 rounded-sm px-4 py-2 hover:bg-slate-100">Cancel</button>
              <button onClick={saveInlineCreate} data-testid={`${testid}-new-save`}
                      className="text-sm bg-slate-900 hover:bg-blue-700 text-white rounded-sm px-4 py-2">
                Create &amp; link
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function FieldRow({ label, value, onChange, testid }) {
  return (
    <div>
      <div className="label-micro">{label}</div>
      <input value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
             className="mt-1 w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"/>
    </div>
  );
}

function RecordEditor({ kind, initial, title, onCancel, onSave, users = [], defaultOwnerId = "" }) {
  const [draft, setDraft] = useState(initial || {});
  const [companyId, setCompanyId] = useState("");
  const [contactSelections, setContactSelections] = useState([]); // [{hs_id,label,subtitle}]
  const [contactDraft, setContactDraft] = useState("");
  const [ownerId, setOwnerId] = useState(defaultOwnerId);
  const [pipelines, setPipelines] = useState([]);
  const [pipelineId, setPipelineId] = useState("");
  const [err, setErr] = useState("");
  useEffect(() => {
    if (kind !== "deals") return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/crm/pipelines");
        if (cancelled) return;
        const list = data?.pipelines || [];
        setPipelines(list);
        // Default to the first pipeline (matches backend server-side default)
        if (list.length && !pipelineId) setPipelineId(list[0].id);
      } catch { /* silent — server will fill defaults */ }
    })();
    return () => { cancelled = true; };
  }, [kind]); // eslint-disable-line
  const addContact = async (id) => {
    if (!id) return;
    if (contactSelections.some(c => c.hs_id === id)) { setContactDraft(""); return; }
    let label = id, subtitle = "";
    try {
      const { data } = await api.get(`/crm/contacts/${id}`);
      const p = data?.record?.properties || {};
      label = rowTitle("contacts", p) || id;
      subtitle = p.email || p.jobtitle || p.phone || "";
    } catch { /* keep id */ }
    setContactSelections([...contactSelections, { hs_id: id, label, subtitle }]);
    setContactDraft("");
  };
  const removeContact = (id) => setContactSelections(contactSelections.filter(c => c.hs_id !== id));
  const handleSave = () => {
    if (kind === "deals") {
      if (!draft.dealname || !draft.dealname.trim()) { setErr("Deal name is required."); return; }
      if (!companyId) { setErr("A deal must be linked to a company."); return; }
      if (contactSelections.length === 0) { setErr("A deal must be linked to at least one contact."); return; }
      setErr("");
      const props = pipelineId ? { ...draft, pipeline: pipelineId } : { ...draft };
      onSave({ properties: props, associations: { companies: [companyId], contacts: contactSelections.map(c => c.hs_id) }, owner_user_id: ownerId });
      return;
    }
    onSave({ properties: draft });
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="crm-create-modal">
      <div className="absolute inset-0 bg-slate-900/50" onClick={onCancel}/>
      <div className="relative bg-white rounded-sm shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-3 flex items-center justify-between">
          <h2 className="font-display font-bold text-lg">{title}</h2>
          <button onClick={onCancel} className="p-1.5 rounded-sm hover:bg-slate-100"><X size={16}/></button>
        </div>
        <div className="p-5 space-y-4">
          {kind === "deals" && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pb-4 border-b border-slate-200">
              <div>
                <div className="label-micro">Company <span className="text-red-600">*</span></div>
                <AssociationPicker kind="companies" value={companyId} onChange={setCompanyId} allowCreate
                                   testid="deal-company-picker" placeholder="Search companies…"/>
              </div>
              <div>
                <div className="label-micro">Pipeline</div>
                <select value={pipelineId} onChange={(e) => setPipelineId(e.target.value)}
                        data-testid="deal-pipeline-picker"
                        className="mt-1 w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  {pipelines.length === 0 && <option value="">— Loading pipelines… —</option>}
                  {pipelines.map(p => <option key={p.id} value={p.id}>{p.label || p.id}{p.count != null ? ` · ${p.count} deals` : ""}</option>)}
                </select>
                <div className="text-[11px] text-slate-500 italic mt-1">Deal lands on this pipeline's first stage. Switch anytime from the drawer.</div>
              </div>

              <div>
                <div className="label-micro">Contacts <span className="text-red-600">*</span> <span className="text-slate-400 font-normal">(one or more)</span></div>
                {contactSelections.length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-1.5 mb-1.5" data-testid="deal-contact-chips">
                    {contactSelections.map(c => (
                      <span key={c.hs_id} className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-sm px-2 py-1 text-[11px] font-semibold max-w-full" data-testid={`deal-contact-chip-${c.hs_id}`}>
                        <span className="truncate max-w-[220px]" title={c.subtitle || c.label}>{c.label}</span>
                        <button onClick={() => removeContact(c.hs_id)} className="text-emerald-600 hover:text-red-600 shrink-0" data-testid={`deal-contact-chip-remove-${c.hs_id}`}><X size={10}/></button>
                      </span>
                    ))}
                  </div>
                )}
                <AssociationPicker kind="contacts" value={contactDraft} onChange={addContact} allowCreate
                                   testid="deal-contact-picker" placeholder={contactSelections.length ? "Add another contact…" : "Search contacts…"}/>
              </div>
              <div className="sm:col-span-2">
                <div className="label-micro">Deal owner</div>
                <select value={ownerId} onChange={(e) => setOwnerId(e.target.value)}
                        data-testid="deal-owner-picker"
                        className="mt-1 w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="">— Unassigned —</option>
                  {users.map(u => <option key={u.id} value={u.id}>{`${u.full_name || u.email} · ${u.email}`}</option>)}
                </select>
                <div className="text-[11px] text-slate-500 italic mt-1">Defaults to you. Pick a teammate to reassign.</div>
              </div>
              <div className="sm:col-span-2 text-[11px] text-slate-500 italic">Every deal must be linked to a company and at least one contact. Use "+ Create new" inside each picker to add them inline without leaving this form.</div>
            </div>
          )}
          <EditorFields kind={kind} draft={draft} setDraft={setDraft}/>
          {err && <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-sm px-3 py-2" data-testid="crm-create-err">{err}</div>}
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onCancel} className="text-sm border border-slate-300 rounded-sm px-4 py-2 hover:bg-slate-100">Cancel</button>
          <button onClick={handleSave} data-testid="crm-create-save" className="text-sm bg-slate-900 hover:bg-blue-700 text-white rounded-sm px-4 py-2 transition-colors">Create</button>
        </div>
      </div>
    </div>
  );
}

function KanbanBoard({ items, columns, onOpen, onStageChange, onCreateInStage, onColumnsChanged, currentPipelineId }) {
  const [dragged, setDragged] = useState(null);
  const [newColName, setNewColName] = useState("");
  const [showAddCol, setShowAddCol] = useState(false);
  const [kanbanQuery, setKanbanQuery] = useState("");
  const scrollRef = useRef(null);
  const panRef = useRef({ active: false, startX: 0, startScroll: 0, moved: 0 });

  // Attach a NON-passive wheel listener via the DOM API. React's synthetic
  // onWheel is passive-by-default in modern versions, which lets the browser
  // scroll the page vertically before our handler can flip deltaY -> scrollLeft.
  // Native addEventListener with { passive: false } gives us full control on
  // Windows Chrome / Edge where the trackpad-style shim doesn't run.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const handler = (e) => {
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) return;
      if (e.deltaY === 0) return;
      e.preventDefault();
      el.scrollLeft += e.deltaY;
    };
    el.addEventListener("wheel", handler, { passive: false });
    return () => el.removeEventListener("wheel", handler);
  }, []);

  // Click-and-drag panning on empty board area (not on a card, header, or button).
  const onMouseDown = (e) => {
    const el = scrollRef.current;
    if (!el) return;
    if (e.button !== 0) return;
    // Skip if starting on an interactive element — cards, buttons, inputs, etc.
    if (e.target.closest("button, a, input, textarea, select, [draggable='true'], [data-testid^='kanban-card-']")) return;
    panRef.current = { active: true, startX: e.clientX, startScroll: el.scrollLeft, moved: 0 };
    el.style.cursor = "grabbing";
  };
  const onMouseMove = (e) => {
    if (!panRef.current.active) return;
    const el = scrollRef.current;
    if (!el) return;
    const dx = e.clientX - panRef.current.startX;
    panRef.current.moved = Math.abs(dx);
    el.scrollLeft = panRef.current.startScroll - dx;
  };
  const endPan = () => {
    const el = scrollRef.current;
    if (el) el.style.cursor = "";
    panRef.current.active = false;
  };

  const filteredItems = useMemo(() => {
    const q = kanbanQuery.trim().toLowerCase();
    if (!q) return items;
    return items.filter(d => {
      const name = (d.properties?.dealname || "").toLowerCase();
      const client = (d._client_name || "").toLowerCase();
      return name.includes(q) || client.includes(q);
    });
  }, [items, kanbanQuery]);

  const grouped = useMemo(() => {
    const cols = (columns || []);
    const firstId = cols[0]?.id;
    const byCol = {};
    cols.forEach(c => { byCol[c.id] = { ...c, deals: [] }; });
    filteredItems.forEach(d => {
      let cid = d.properties?.kanban_column;
      if (!cid || !byCol[cid]) cid = firstId;
      if (cid && byCol[cid]) byCol[cid].deals.push(d);
    });
    return cols.map(c => {
      const col = byCol[c.id];
      col.total = col.deals.reduce((s, d) => s + Number(d.properties?.amount || 0), 0);
      return col;
    });
  }, [filteredItems, columns]);

  const daysInStage = (d) => {
    // Order: stage_updated_at (set on kanban_column change), else HubSpot's
    // date_entered_current_stage, else fallback to created_at (safer than
    // updated_at, which can be moved by ANY edit and reset the "days" to 0
    // every time a deal is touched — the very bug users reported).
    const iso = d.stage_updated_at
      || d.properties?.hs_v2_date_entered_current_stage
      || d.created_at
      || d.properties?.createdate;
    if (!iso) return null;
    return Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86400000));
  };
  const staleClass = (days) => {
    if (days === null || days === undefined) return "bg-slate-100 text-slate-500";
    if (days < 7)  return "bg-emerald-100 text-emerald-700";
    if (days < 14) return "bg-amber-100 text-amber-700";
    if (days < 30) return "bg-orange-100 text-orange-700";
    return "bg-red-100 text-red-700";
  };
  const cardBorderClass = (days) => {
    if (days === null || days === undefined) return "border-slate-200";
    if (days < 7)  return "border-slate-200";
    if (days < 14) return "border-amber-200";
    if (days < 30) return "border-orange-300";
    return "border-red-400";
  };

  const renameCol = async (col) => {
    const name = window.prompt("Rename column", col.name);
    if (!name || name === col.name) return;
    try { await api.patch(`/crm/kanban/columns/${col.id}`, { name }); toast.success("Renamed"); onColumnsChanged(); }
    catch (e) { toast.error("Rename failed"); }
  };
  const deleteCol = async (col) => {
    if (!window.confirm(`Delete column "${col.name}"?\n\nDeals in this column will move to "Unassigned" — they won't be lost.`)) return;
    try { await api.delete(`/crm/kanban/columns/${col.id}`); toast.success("Deleted"); onColumnsChanged(); }
    catch (e) { toast.error("Delete failed"); }
  };
  const addCol = async () => {
    if (!newColName.trim()) return;
    try {
      const body = { name: newColName.trim() };
      // If a pipeline is selected, scope the new column to that pipeline so it
      // only appears when browsing that pipeline (fixes bug #4).
      if (currentPipelineId) body.pipeline_id = currentPipelineId;
      await api.post("/crm/kanban/columns", body);
      toast.success("Column added");
      setNewColName(""); setShowAddCol(false); onColumnsChanged();
    } catch (e) { toast.error("Add failed"); }
  };

  if (!columns || columns.length === 0) return <div className="mt-4 text-sm text-slate-500 p-6">Loading board…</div>;

  return (
    <div className="mt-4" data-testid="kanban-board">
      <div className="mb-3">
        <div className="relative max-w-md">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
          <input value={kanbanQuery} onChange={(e) => setKanbanQuery(e.target.value)}
                 placeholder="Filter cards by deal or client name..."
                 data-testid="kanban-search"
                 className="w-full border border-slate-300 rounded-sm pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"/>
        </div>
        {kanbanQuery && <div className="text-[11px] text-slate-500 mt-1">Showing {filteredItems.length} of {items.length} deals matching "<b>{kanbanQuery}</b>"</div>}
      </div>
      <div ref={scrollRef} onMouseDown={onMouseDown} onMouseMove={onMouseMove}
           onMouseUp={endPan} onMouseLeave={endPan}
           className="overflow-x-scroll pb-4 kanban-scroll select-none cursor-grab"
           data-testid="kanban-scroll">
      <div className="flex gap-3 min-w-max">
        {grouped.map(col => {
          const c = stageColor(col.color);
          return (
          <div key={col.id} className={`w-72 shrink-0 ${c.colBg} rounded-sm border border-slate-200 flex flex-col overflow-hidden`}
               data-testid={`kanban-col-${col.id}`}
               onDragOver={(e) => e.preventDefault()}
               onDrop={(e) => { e.preventDefault(); if (dragged && (dragged.properties?.kanban_column !== col.id)) onStageChange(dragged, col.id); setDragged(null); }}>
            <div className={`h-1 ${c.strip}`} data-testid={`kanban-col-strip-${col.id}`}/>
            <div className={`px-3 py-2.5 border-b border-slate-200 sticky top-0 ${c.colHead}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-1.5 flex-1 min-w-0">
                  <span className={`w-2 h-2 rounded-full ${c.dot} shrink-0`}/>
                  <button onClick={() => renameCol(col)} data-testid={`kanban-col-rename-${col.id}`}
                          className="font-semibold text-sm text-slate-800 truncate text-left hover:text-blue-700 flex-1">{col.name}</button>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <span className="text-[10px] font-bold px-1.5 py-0.5 bg-white text-slate-700 rounded-sm">{col.deals.length}</span>
                  {onCreateInStage && (
                    <button onClick={() => onCreateInStage(col.id)} data-testid={`kanban-add-${col.id}`}
                            title={`Add deal to "${col.name}"`}
                            className="w-5 h-5 rounded-sm bg-white border border-slate-300 hover:bg-blue-600 hover:text-white hover:border-blue-600 text-slate-600 grid place-items-center transition-colors">
                      <Plus size={11} weight="bold"/>
                    </button>
                  )}
                  <button onClick={() => deleteCol(col)} data-testid={`kanban-col-del-${col.id}`} title="Delete column"
                          className="w-5 h-5 rounded-sm bg-white border border-slate-300 hover:bg-red-500 hover:text-white hover:border-red-500 text-slate-500 grid place-items-center transition-colors">
                    <X size={10}/>
                  </button>
                </div>
              </div>
              <div className="text-[11px] text-slate-500 font-semibold mt-0.5">{fmtINR(col.total)}</div>
            </div>
            <div className="p-2 space-y-2 flex-1 min-h-[100px]">
              {col.deals.map(d => {
                const days = daysInStage(d);
                const client = d._client_name;
                const followup = d.properties?.next_followup_date;
                const latestNote = (d.followup_notes && d.followup_notes[0]) || null;
                const followupOverdue = followup && new Date(followup) < new Date();
                return (
                  <div key={d.hs_id} draggable
                       onDragStart={() => setDragged(d)} onDragEnd={() => setDragged(null)}
                       onClick={() => onOpen(d)}
                       className={`bg-white border border-slate-200 rounded-md p-3 cursor-grab active:cursor-grabbing hover:border-blue-400 hover:shadow-md transition ${dragged?.hs_id === d.hs_id ? "opacity-40" : ""}`}
                       data-testid={`kanban-card-${d.hs_id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="text-[13px] font-semibold text-slate-900 leading-tight line-clamp-2">{d.properties?.dealname || "(Untitled)"}</div>
                        {client && <div className="text-[11px] text-slate-500 truncate mt-1">{client}</div>}
                      </div>
                      {days !== null && (
                        <span className={`text-[10px] font-bold w-7 h-7 grid place-items-center rounded-full shrink-0 ${staleClass(days)}`} title={`${days} days in stage`}>{days}</span>
                      )}
                    </div>
                    <div className="mt-2 text-[11px] text-slate-500 space-y-0.5">
                      {(d.properties?.createdate || d.created_at) && <div>Create date: {new Date(d.properties?.createdate || d.created_at).toLocaleDateString()}</div>}
                      {d.properties?.closedate && <div>Close date: {new Date(d.properties.closedate).toLocaleDateString()}</div>}
                    </div>
                    <div className="mt-2 flex items-center justify-between gap-2">
                      <div className="font-display font-black text-[15px] text-slate-900">{fmtINR(d.properties?.amount)}</div>
                      {d.owner_name && (
                        <div className="flex items-center gap-1.5" data-testid={`kanban-owner-${d.hs_id}`} title={d.owner_name}>
                          <span className="w-6 h-6 rounded-full bg-slate-800 text-white grid place-items-center text-[10px] font-bold shrink-0">{d.owner_name.slice(0,1).toUpperCase()}</span>
                        </div>
                      )}
                    </div>
                    {(followup || latestNote || isLocal(d.hs_id)) && (
                      <div className="mt-2 pt-2 border-t border-slate-100 space-y-1">
                        {isLocal(d.hs_id) && <span className="text-[8px] font-bold px-1 py-0.5 bg-blue-100 text-blue-700 rounded-sm">NATIVE</span>}
                        {followup && (
                          <div className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-sm inline-block ${followupOverdue ? "bg-red-100 text-red-700" : "bg-blue-100 text-blue-700"}`}>
                            📅 Follow-up {new Date(followup).toLocaleDateString()}
                          </div>
                        )}
                        {latestNote && (
                          <div className="text-[10px] text-slate-500 italic border-l-2 border-slate-200 pl-1.5 line-clamp-2">
                            "{latestNote.note}" — <b>{latestNote.author}</b>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
              {onCreateInStage && col.deals.length === 0 && (
                <button onClick={() => onCreateInStage(col.id)} data-testid={`kanban-add-empty-${col.id}`}
                        className="w-full text-xs text-slate-500 border border-dashed border-slate-300 rounded-sm py-2 hover:border-blue-400 hover:text-blue-700 transition-colors">
                  + New deal
                </button>
              )}
            </div>
          </div>
          );
        })}
        {/* Add column */}
        <div className="w-64 shrink-0">
          {showAddCol ? (
            <div className="bg-white border border-slate-300 rounded-sm p-3">
              <input autoFocus value={newColName} onChange={(e) => setNewColName(e.target.value)}
                     onKeyDown={(e) => e.key === "Enter" && addCol()}
                     placeholder="Column name" data-testid="kanban-new-col-input"
                     className="w-full border border-slate-300 rounded-sm px-2 py-1.5 text-sm"/>
              <div className="flex gap-1 mt-2">
                <button onClick={addCol} data-testid="kanban-new-col-save" className="text-xs bg-slate-900 text-white rounded-sm px-3 py-1 hover:bg-blue-700">Add</button>
                <button onClick={() => { setShowAddCol(false); setNewColName(""); }} className="text-xs border border-slate-300 rounded-sm px-3 py-1">Cancel</button>
              </div>
            </div>
          ) : (
            <button onClick={() => setShowAddCol(true)} data-testid="kanban-add-column"
                    className="w-full text-sm text-slate-500 border border-dashed border-slate-300 rounded-sm py-3 hover:border-blue-400 hover:text-blue-700 transition-colors">
              + Add column
            </button>
          )}
        </div>
      </div>
      </div>
    </div>
  );
}

function ChangeOwnerButton({ dealHsId, currentOwnerId, currentOwnerName, users, onChanged }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    if (open) document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);
  const pick = async (uid) => {
    if (uid === currentOwnerId) { setOpen(false); return; }
    setBusy(true);
    try {
      const { data } = await api.patch(`/crm/deals/${dealHsId}/owner`, { owner_user_id: uid });
      toast.success(uid ? `Owner set to ${data.owner_name || "teammate"}` : "Owner cleared");
      onChanged(data); setOpen(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Reassign failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen(!open)} disabled={busy} data-testid="crm-change-owner-btn"
              className="text-xs inline-flex items-center gap-1 border border-slate-300 rounded-sm px-2.5 py-1.5 hover:bg-slate-900 hover:text-white transition-colors disabled:opacity-50">
        <UserSwitch size={12}/><span className="truncate max-w-[140px]">Owner: {currentOwnerName || "Unassigned"}</span><CaretDown size={10}/>
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 w-64 bg-white border border-slate-200 rounded-sm shadow-lg z-50 max-h-72 overflow-y-auto" data-testid="crm-change-owner-menu">
          <button onClick={() => pick("")} data-testid="crm-owner-unassign"
                  className={`w-full text-left px-3 py-2 text-xs hover:bg-slate-100 flex items-center gap-2 ${!currentOwnerId ? "bg-slate-100 font-semibold" : ""}`}>
            <span className="w-6 h-6 rounded-full bg-slate-200 grid place-items-center text-slate-500 text-[9px] font-bold">?</span>Unassigned
          </button>
          <div className="border-t border-slate-100"/>
          {users.map(u => (
            <button key={u.id} onClick={() => pick(u.id)} data-testid={`crm-owner-option-${u.id}`}
                    className={`w-full text-left px-3 py-2 text-xs hover:bg-slate-100 flex items-center gap-2 ${u.id === currentOwnerId ? "bg-slate-100 font-semibold" : ""}`}>
              <span className="w-6 h-6 rounded-full bg-slate-800 text-white grid place-items-center text-[9px] font-bold">{(u.full_name || u.email).slice(0, 1).toUpperCase()}</span>
              <div className="min-w-0">
                <div className="truncate font-semibold text-slate-900">{u.full_name || u.email}</div>
                {u.full_name && <div className="text-[10px] text-slate-500 truncate">{u.email}</div>}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}


function DetailBody({ kind, detail, columns = [], pipelines = [], onColumnChange, onPipelineChanged, onAssociationsChanged }) {
  const props = detail.record?.properties || {};
  const record = detail.record || {};
  const [showAll, setShowAll] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(true);
  const [notes, setNotes] = useState(record.followup_notes || []);
  const [newNote, setNewNote] = useState("");
  const [newSubject, setNewSubject] = useState("");
  const [composerKind, setComposerKind] = useState("notes");
  const [activityTab, setActivityTab] = useState("all");
  const entries = Object.entries(props).filter(([, v]) => v !== null && v !== "");
  const displayed = showAll ? entries : entries.slice(0, 12);

  const addNote = async () => {
    if (!newNote.trim()) return;
    try {
      const payload = { note: newNote, kind: composerKind };
      if (newSubject.trim()) payload.subject = newSubject.trim();
      const { data } = await api.post(`/crm/deals/${record.hs_id}/notes`, payload);
      setNotes([data, ...notes]); setNewNote(""); setNewSubject("");
      toast.success(`${composerKind === "notes" ? "Note" : composerKind.slice(0, -1).replace(/^./, c => c.toUpperCase())} logged`);
    } catch (e) { toast.error("Failed to log activity"); }
  };
  const removeNote = async (id) => {
    try { await api.delete(`/crm/deals/${record.hs_id}/notes/${id}`); setNotes(notes.filter(n => n.id !== id)); }
    catch (e) { toast.error("Delete failed"); }
  };
  const openComposer = (kind) => {
    setComposerKind(kind);
    if (activityTab !== "all" && activityTab !== kind) setActivityTab(kind);
    setTimeout(() => document.querySelector('[data-testid="crm-note-input"]')?.focus(), 50);
  };

  if (kind !== "deals") return <NonDealDetailBody detail={detail} kind={kind}/>;

  // HubSpot-style deal view
  const currentColId = props.kanban_column || (columns[0]?.id || "");
  const currentPipelineId = props.pipeline || columns.find(c => c.id === currentColId)?.hs_pipeline_id || "";
  const pipelineLabel = pipelines.find(p => p.id === currentPipelineId)?.label || "Pipeline";
  const stageColumns = currentPipelineId
    ? columns.filter(c => !c.hs_pipeline_id || c.hs_pipeline_id === currentPipelineId)
    : columns;
  const currentIdx = stageColumns.findIndex(c => c.id === currentColId);
  const currentColName = stageColumns[currentIdx]?.name || "Stage";

  const notesActivities = notes.map(n => ({ id: n.id, kind: n.kind || "notes", body: n.note, subject: n.subject, author: n.author, at: n.at, canDelete: true }));
  const hsActivities = (detail.activities || []).map(a => {
    const p = a.properties || {};
    const body = p.hs_note_body ? strip(p.hs_note_body) : p.hs_email_subject || p.hs_meeting_title || p.hs_task_subject || p.hs_call_title || "";
    return { id: a.id || a.hs_id, kind: a.kind, body, author: "", at: a.updated_at, canDelete: false };
  });
  const allActivity = [...notesActivities, ...hsActivities].sort((a, b) => new Date(b.at) - new Date(a.at));
  const filteredActivity = activityTab === "all" ? allActivity : allActivity.filter(a => a.kind === activityTab);
  const activityByMonth = {};
  filteredActivity.forEach(a => {
    const key = a.at ? new Date(a.at).toLocaleDateString("en-US", { month: "long", year: "numeric" }) : "Undated";
    activityByMonth[key] = activityByMonth[key] || [];
    activityByMonth[key].push(a);
  });

  const contacts = detail.related?.contacts || [];
  const companies = detail.related?.companies || [];
  const activityTabs = [
    { key: "all", label: `All activities`, count: allActivity.length },
    { key: "notes", label: "Notes", count: notesActivities.length },
    { key: "emails", label: "Emails", count: hsActivities.filter(a => a.kind === "emails").length },
    { key: "calls", label: "Calls", count: hsActivities.filter(a => a.kind === "calls").length },
    { key: "tasks", label: "Tasks", count: hsActivities.filter(a => a.kind === "tasks").length },
    { key: "meetings", label: "Meetings", count: hsActivities.filter(a => a.kind === "meetings").length },
  ];

  return (
    <div className="pb-8" data-testid="deal-detail-hubspot">
      {/* Header block */}
      <div className="bg-white border-b border-slate-200 px-6 pt-5 pb-4">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-12 h-12 rounded-md bg-gradient-to-br from-blue-500 to-blue-700 text-white grid place-items-center font-black text-lg shrink-0">
            {(props.dealname || "D").slice(0, 1).toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-display font-black text-xl text-slate-900 truncate">{props.dealname || "(Untitled deal)"}</div>
            {record._client_name && <div className="text-sm text-slate-500 mt-0.5">{record._client_name}</div>}
          </div>
        </div>
        {/* Key stats strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          <div><div className="label-micro text-slate-500">Amount</div><div className="font-display font-black text-lg mt-0.5">{fmtINR(props.amount)}</div></div>
          <div><div className="label-micro text-slate-500">Close date</div><div className="text-sm font-semibold mt-1.5">{props.closedate || "—"}</div></div>
          <div><div className="label-micro text-slate-500">Pipeline</div>
            <div className="mt-1"><PipelineSwitcher record={record} pipelines={pipelines} pipelineLabel={pipelineLabel} onPipelineChanged={onPipelineChanged}/></div>
          </div>
          <div><div className="label-micro text-slate-500">Owner</div>
            {record.owner_name ? (
              <div className="flex items-center gap-1.5 mt-1" data-testid="crm-deal-owner">
                <span className="w-6 h-6 rounded-full bg-slate-800 text-white grid place-items-center text-[10px] font-bold">{record.owner_name.slice(0,1).toUpperCase()}</span>
                <span className="text-sm font-semibold text-slate-800 truncate">{record.owner_name}</span>
              </div>
            ) : <div className="text-sm text-slate-400 mt-1.5">— Unassigned</div>}
          </div>
        </div>
        {/* Stage stepper */}
        {stageColumns.length > 0 && (
          <div className="mt-2" data-testid="deal-stage-stepper">
            <div className="flex items-center gap-1 overflow-x-auto pb-1">
              {stageColumns.map((c, i) => {
                const isCurrent = i === currentIdx;
                const isDone = currentIdx >= 0 && i < currentIdx;
                return (
                  <button key={c.id} onClick={() => onColumnChange && onColumnChange(record, c.id)}
                          data-testid={`deal-stage-step-${c.id}`}
                          className={`text-[11px] font-semibold px-3 py-2 shrink-0 whitespace-nowrap transition-colors relative
                            ${isCurrent ? "bg-blue-600 text-white" : isDone ? "bg-slate-800 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}
                            ${i === 0 ? "rounded-l-sm" : ""} ${i === stageColumns.length - 1 ? "rounded-r-sm" : ""}`}
                          style={{ clipPath: i === stageColumns.length - 1 ? "polygon(0 0, 100% 0, 100% 100%, 0 100%, 12px 50%)" : i === 0 ? "polygon(0 0, calc(100% - 12px) 0, 100% 50%, calc(100% - 12px) 100%, 0 100%)" : "polygon(0 0, calc(100% - 12px) 0, 100% 50%, calc(100% - 12px) 100%, 0 100%, 12px 50%)" }}>
                    {c.name}
                  </button>
                );
              })}
            </div>
            <div className="text-[11px] text-slate-500 mt-1">Currently in <b className="text-slate-800">{currentColName}</b>. Click any stage to move this deal.</div>
          </div>
        )}
        {/* Quick actions */}
        <div className="flex items-center gap-1 mt-4 border-t border-slate-100 pt-3">
          <QuickAction icon={Note} label="Note" active={composerKind === "notes"} onClick={() => openComposer("notes")}/>
          <QuickAction icon={EnvelopeSimple} label="Email" active={composerKind === "emails"} onClick={() => openComposer("emails")}/>
          <QuickAction icon={Phone} label="Call" active={composerKind === "calls"} onClick={() => openComposer("calls")}/>
          <QuickAction icon={CheckSquare} label="Task" active={composerKind === "tasks"} onClick={() => openComposer("tasks")}/>
          <QuickAction icon={CalendarBlank} label="Meeting" active={composerKind === "meetings"} onClick={() => openComposer("meetings")}/>
          <QuickAction icon={DotsThree} label="More" onClick={() => toast.info("More actions coming soon")}/>
        </div>
      </div>

      {/* Body — 2-col: activity + about (left) / associations rail (right) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 p-6">
        <div className="lg:col-span-2 space-y-4">
          {/* Activity tabs */}
          <div className="bg-white rounded-md border border-slate-200 overflow-hidden">
            <div className="border-b border-slate-200 flex items-center overflow-x-auto" data-testid="deal-activity-tabs">
              {activityTabs.map(t => (
                <button key={t.key} onClick={() => setActivityTab(t.key)}
                        data-testid={`deal-activity-tab-${t.key}`}
                        className={`text-xs font-semibold px-3 py-2.5 border-b-2 shrink-0 transition-colors ${activityTab === t.key ? "border-blue-600 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
                  {t.label}{t.count > 0 && <span className="ml-1 text-slate-400">({t.count})</span>}
                </button>
              ))}
            </div>
            {/* Composer */}
            {(() => {
              const shownWhen = activityTab === "all" || activityTab === composerKind;
              if (!shownWhen) return null;
              const kindMeta = {
                notes:    { label: "note",    bg: "bg-amber-50/60",    placeholder: "Log a note about this deal…",                       button: "Save note",    subject: false },
                emails:   { label: "email",   bg: "bg-blue-50/60",     placeholder: "What did you discuss over email? Paste key points…", button: "Log email",    subject: true  },
                calls:    { label: "call",    bg: "bg-emerald-50/60",  placeholder: "Call summary — who you spoke to, outcome, next step…", button: "Log call",   subject: false },
                tasks:    { label: "task",    bg: "bg-purple-50/60",   placeholder: "Describe the task and its owner…",                    button: "Save task",    subject: true  },
                meetings: { label: "meeting", bg: "bg-slate-100/70",   placeholder: "Meeting notes — attendees, decisions, action items…", button: "Log meeting",  subject: true  },
              }[composerKind] || {};
              return (
                <div className={`p-4 border-b border-slate-100 ${kindMeta.bg}`} data-testid={`deal-composer-${composerKind}`}>
                  {kindMeta.subject && (
                    <input value={newSubject} onChange={(e) => setNewSubject(e.target.value)}
                           placeholder={composerKind === "emails" ? "Email subject (optional)" : composerKind === "tasks" ? "Task title (optional)" : "Meeting subject (optional)"}
                           data-testid="crm-note-subject"
                           className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white mb-2 font-semibold"/>
                  )}
                  <textarea rows={2} value={newNote} onChange={(e) => setNewNote(e.target.value)}
                            placeholder={kindMeta.placeholder} data-testid="crm-note-input"
                            className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"/>
                  <div className="flex justify-end mt-2">
                    <button onClick={addNote} data-testid="crm-note-add" disabled={!newNote.trim()}
                            className="text-xs bg-slate-900 hover:bg-blue-700 disabled:opacity-40 text-white rounded-sm px-4 py-2 transition-colors font-semibold">{kindMeta.button}</button>
                  </div>
                </div>
              );
            })()}
            {/* Activity feed grouped by month */}
            <div className="p-4">
              {Object.keys(activityByMonth).length === 0 && <div className="text-xs text-slate-500 italic text-center py-6">No {activityTab === "all" ? "activities" : activityTab} yet.</div>}
              {Object.entries(activityByMonth).map(([month, items]) => (
                <div key={month} className="mb-4">
                  <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 pb-2 border-b border-slate-100 mb-3">{month}</div>
                  <ul className="space-y-3" data-testid="crm-notes-list">
                    {items.map(a => (
                      <li key={`${a.kind}-${a.id}`} className="group flex gap-3" data-testid={`activity-${a.id}`}>
                        <div className={`w-7 h-7 rounded-full grid place-items-center shrink-0 ${a.kind === "notes" ? "bg-amber-100 text-amber-700" : a.kind === "emails" ? "bg-blue-100 text-blue-700" : a.kind === "calls" ? "bg-emerald-100 text-emerald-700" : a.kind === "tasks" ? "bg-purple-100 text-purple-700" : "bg-slate-100 text-slate-700"}`}>
                          {a.kind === "notes" && <Note size={13} weight="bold"/>}
                          {a.kind === "emails" && <EnvelopeSimple size={13} weight="bold"/>}
                          {a.kind === "calls" && <Phone size={13} weight="bold"/>}
                          {a.kind === "tasks" && <CheckSquare size={13} weight="bold"/>}
                          {a.kind === "meetings" && <CalendarBlank size={13} weight="bold"/>}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <div className="text-[11px] text-slate-500">
                              <b className="text-slate-800 capitalize">{ACTIVITY_LABEL[a.kind] || a.kind}</b>
                              {a.author && <> by <b className="text-slate-800">{a.author}</b></>}
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-[10px] text-slate-500">{a.at && new Date(a.at).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })}</span>
                              {a.canDelete && <button onClick={() => removeNote(a.id)} className="text-[10px] text-red-600 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`crm-note-del-${a.id}`}>Delete</button>}
                            </div>
                          </div>
                          {a.subject && <div className="text-sm font-semibold text-slate-900 mt-0.5">{a.subject}</div>}
                          {a.body && <div className="text-sm text-slate-800 mt-1 whitespace-pre-wrap">{a.body}</div>}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          {/* About this deal (collapsible) */}
          <div className="bg-white rounded-md border border-slate-200">
            <button onClick={() => setAboutOpen(!aboutOpen)} className="w-full flex items-center gap-2 px-4 py-3 text-left" data-testid="deal-about-toggle">
              {aboutOpen ? <CaretDown size={14}/> : <CaretRight size={14}/>}
              <span className="font-semibold text-sm text-slate-900">About this deal</span>
              <span className="text-[10px] text-slate-500 ml-auto">{entries.length} fields</span>
            </button>
            {aboutOpen && (
              <div className="border-t border-slate-100 p-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
                  <AboutField label="Deal owner" value={record.owner_name || "—"}/>
                  <AboutField label="Deal stage" value={currentColName}/>
                  <AboutField label="Amount" value={fmtINR(props.amount)}/>
                  <AboutField label="Close date" value={props.closedate || "—"}/>
                  <AboutField label="Priority" value={props.hs_priority || "—"}/>
                  <AboutField label="Next follow-up" value={props.next_followup_date || "—"}/>
                </div>
                <div className="mt-4 pt-4 border-t border-slate-100">
                  <div className="label-micro mb-2">All fields ({entries.length})</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-xs">
                    {displayed.map(([k, v]) => (
                      <div key={k} className="border-b border-slate-100 pb-1">
                        <div className="text-slate-500 text-[10px] uppercase tracking-wider font-semibold">{k}</div>
                        <div className="text-slate-800 break-words">{String(v).slice(0, 300)}</div>
                      </div>
                    ))}
                  </div>
                  {entries.length > 12 && <button onClick={() => setShowAll(!showAll)} className="text-xs text-blue-700 hover:underline mt-2" data-testid="crm-show-all-fields">{showAll ? "Show less" : `Show all ${entries.length} fields`}</button>}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right rail — associations */}
        <div className="space-y-4">
          <AssocRailSection title="Contacts" count={contacts.length} items={contacts} kind="contacts" empty="See the people associated with this deal."
                            editable dealHsId={record.hs_id} onChanged={onAssociationsChanged}
                            primaryContactHsId={record.primary_contact_hs_id}/>
          <AssocRailSection title="Companies" count={companies.length} items={companies} kind="companies" empty="See the businesses or organizations associated with this record."/>
          <DealTicketsCard dealHsId={record.hs_id} dealName={props.dealname}/>
        </div>
      </div>
    </div>
  );
}

function QuickAction({ icon: Icon, label, onClick, active }) {
  return (
    <button onClick={onClick} data-testid={`deal-quick-${label.toLowerCase()}`}
            className={`flex flex-col items-center gap-1 px-4 py-2 rounded-sm transition-colors ${active ? "text-blue-700 hover:bg-blue-50" : "text-slate-600 hover:bg-slate-100"}`}>
      <span className={`w-8 h-8 rounded-full grid place-items-center ${active ? "bg-blue-100" : "bg-slate-100"}`}>
        <Icon size={16} weight="bold"/>
      </span>
      <span className="text-[10px] font-semibold">{label}</span>
    </button>
  );
}

function AboutField({ label, value }) {
  return (
    <div>
      <div className="text-[11px] text-slate-500">{label}</div>
      <div className="text-sm font-semibold text-slate-800 mt-0.5">{value}</div>
    </div>
  );
}

function DealTicketsCard({ dealHsId, dealName }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(true);
  const nav = useNavigate();
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/tickets", { params: { deal_hs_id: dealHsId } });
        setRows(data.tickets || []);
      } catch {} finally { setBusy(false); }
    })();
  }, [dealHsId]);
  const openCreate = () => {
    const qs = new URLSearchParams({ deal: dealHsId, deal_name: dealName || "" });
    nav(`/tickets?${qs.toString()}`);
  };
  return (
    <div className="bg-white rounded-md border border-slate-200" data-testid="deal-tickets-card">
      <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
        <div className="font-semibold text-sm text-slate-900">Tickets <span className="text-slate-500 font-normal">({rows.length})</span></div>
        <button onClick={openCreate} data-testid="deal-new-ticket-btn"
                className="text-[10px] font-semibold inline-flex items-center gap-0.5 border border-slate-300 rounded-sm px-2 py-1 hover:bg-slate-900 hover:text-white transition-colors"><Plus size={10}/>New</button>
      </div>
      <div className="p-4">
        {busy && <div className="text-[11px] text-slate-500 italic">Loading…</div>}
        {!busy && rows.length === 0 && <div className="text-[11px] text-slate-500">No tickets linked yet. Hit <b>New</b> to open one against this deal.</div>}
        <ul className="space-y-2">
          {rows.map(t => (
            <li key={t.id} onClick={() => nav(`/tickets?open=${t.id}`)}
                data-testid={`deal-ticket-${t.id}`}
                className={`cursor-pointer border rounded-sm p-2 transition-colors ${t.overdue ? "border-red-300 bg-red-50 hover:bg-red-100" : "border-slate-200 hover:border-blue-400 hover:bg-slate-50"}`}>
              <div className="text-sm font-semibold text-slate-900 truncate">{t.title}</div>
              <div className="text-[10px] text-slate-500 flex items-center gap-2 mt-0.5">
                <span className="uppercase tracking-wider">{t.status.replace("_", " ")}</span>
                {t.assignee_name && <span>· {t.assignee_name}</span>}
                {t.due_date && <span className={t.overdue ? "text-red-700 font-bold" : ""}>· due {t.due_date}</span>}
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}


function AssocRailSection({ title, count, items, kind, empty, editable = false, dealHsId, onChanged, primaryContactHsId }) {
  const [adding, setAdding] = useState(false);
  const [pickerVal, setPickerVal] = useState("");
  const [busy, setBusy] = useState(false);
  // Local mirror of association ids so a fast 2nd add doesn't build off a
  // stale `items` prop and silently unlink previous entries (fix from iter22).
  const [assocIds, setAssocIds] = useState(() => (items || []).map(x => x.hs_id));
  useEffect(() => { setAssocIds((items || []).map(x => x.hs_id)); }, [items]);
  const patchAssoc = async (newIds) => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/crm/deals/${dealHsId}`, { associations: { [kind]: newIds } });
      const serverIds = ((data.associations || {})[kind]) || newIds;
      setAssocIds(serverIds); // trust server, not stale props
      toast.success(`${title} updated`);
      onChanged && onChanged(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
    finally { setBusy(false); }
  };
  const addOne = async (newId) => {
    if (!newId) return;
    if (assocIds.includes(newId)) { setAdding(false); setPickerVal(""); return; }
    await patchAssoc([...assocIds, newId]);
    setAdding(false); setPickerVal("");
  };
  const removeOne = async (rmId) => {
    if (!window.confirm(`Unlink this ${kind === "contacts" ? "contact" : "company"} from the deal?`)) return;
    await patchAssoc(assocIds.filter(x => x !== rmId));
  };
  const setPrimary = async (contactHsId) => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/crm/deals/${dealHsId}/primary-contact`, { contact_hs_id: contactHsId });
      toast.success("Primary contact set");
      onChanged && onChanged(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="bg-white rounded-md border border-slate-200">
      <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
        <div className="font-semibold text-sm text-slate-900">{title} <span className="text-slate-500 font-normal">({assocIds.length})</span></div>
        {editable && !adding && (
          <button onClick={() => setAdding(true)} disabled={busy} data-testid={`assoc-${kind}-add-btn`}
                  className="text-[10px] font-semibold inline-flex items-center gap-0.5 border border-slate-300 rounded-sm px-2 py-1 hover:bg-slate-900 hover:text-white transition-colors disabled:opacity-50"><Plus size={10}/>Add</button>
        )}
      </div>
      <div className="p-4">
        {editable && adding && (
          <div className="mb-3" data-testid={`assoc-${kind}-add-form`}>
            <AssociationPicker kind={kind} value={pickerVal} onChange={addOne} allowCreate
                               testid={`assoc-${kind}-add-picker`} placeholder={`Search ${kind}…`}/>
            <div className="text-right mt-1">
              <button onClick={() => { setAdding(false); setPickerVal(""); }} className="text-[10px] text-slate-500 hover:text-red-600">Cancel</button>
            </div>
          </div>
        )}
        {items.length === 0 && !adding && (
          <div className="text-center py-4">
            <div className="w-10 h-10 rounded-full bg-slate-100 grid place-items-center mx-auto mb-2"><MagnifyingGlass size={16} className="text-slate-400"/></div>
            <div className="text-[11px] text-slate-500">{empty}</div>
          </div>
        )}
        <ul className="space-y-3">
          {items.map(r => {
            const p = r.properties || {};
            const name = rowTitle(kind, p);
            const isPrimary = kind === "contacts" && primaryContactHsId === r.hs_id;
            return (
              <li key={r.hs_id} className="group flex items-start gap-2" data-testid={`assoc-${kind}-${r.hs_id}`}>
                <div className={`w-8 h-8 rounded-full grid place-items-center text-white text-xs font-bold shrink-0 ${kind === "companies" ? "bg-indigo-600" : "bg-emerald-600"}`}>{name.slice(0,1).toUpperCase()}</div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-slate-900 truncate flex items-center gap-1.5">
                    {name}
                    {isPrimary && <span data-testid={`assoc-${kind}-primary-badge-${r.hs_id}`} className="inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wider text-amber-700 bg-amber-100 border border-amber-300 rounded-sm px-1.5 py-0.5"><Star size={8} weight="fill"/>Primary</span>}
                  </div>
                  <div className="text-[11px] text-slate-500 truncate">{kind === "contacts" ? [p.jobtitle, p.email, p.phone].filter(Boolean).join(" · ") || "—" : (p.domain || p.industry || "—")}</div>
                </div>
                {editable && kind === "contacts" && !isPrimary && (
                  <button onClick={() => setPrimary(r.hs_id)} disabled={busy}
                          data-testid={`assoc-contacts-set-primary-${r.hs_id}`}
                          title="Set as primary contact"
                          className="text-slate-400 opacity-0 group-hover:opacity-100 hover:text-amber-600 transition-all disabled:opacity-30 shrink-0"><Star size={13}/></button>
                )}
                {editable && (
                  <button onClick={() => removeOne(r.hs_id)} disabled={busy} data-testid={`assoc-${kind}-remove-${r.hs_id}`}
                          className="text-[10px] text-red-600 opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-30 shrink-0">Unlink</button>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

function PipelineSwitcher({ record, pipelines, pipelineLabel, onPipelineChanged }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const currentId = record?.properties?.pipeline || "";
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    if (open) document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);
  const pick = async (pid) => {
    if (!pid || pid === currentId) { setOpen(false); return; }
    if (!window.confirm("Switching pipeline resets the deal to that pipeline's first stage. Continue?")) return;
    setBusy(true);
    try {
      const { data } = await api.patch(`/crm/deals/${record.hs_id}/pipeline`, { pipeline_id: pid });
      toast.success("Pipeline changed"); onPipelineChanged && onPipelineChanged(data); setOpen(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Change failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="relative inline-block" ref={ref}>
      <button onClick={() => setOpen(!open)} disabled={busy} data-testid="deal-pipeline-switcher"
              className="text-[11px] font-bold px-2 py-0.5 bg-amber-100 text-amber-800 rounded-sm inline-flex items-center gap-1 hover:bg-amber-200 disabled:opacity-50">
        {pipelineLabel}<CaretDown size={10}/>
      </button>
      {open && (
        <div className="absolute left-0 top-full mt-1 w-56 bg-white border border-slate-200 rounded-sm shadow-lg z-50" data-testid="deal-pipeline-menu">
          {(pipelines || []).map(p => (
            <button key={p.id} onClick={() => pick(p.id)}
                    data-testid={`deal-pipeline-option-${p.id}`}
                    className={`w-full text-left px-3 py-2 text-xs hover:bg-slate-100 flex items-center justify-between ${p.id === currentId ? "bg-slate-100 font-semibold" : ""}`}>
              <span className="truncate">{p.label || p.id}</span>
              {p.count != null && <span className="text-[10px] text-slate-500">{p.count}</span>}
            </button>
          ))}
          {(!pipelines || pipelines.length === 0) && <div className="px-3 py-2 text-[11px] text-slate-500 italic">No pipelines available.</div>}
        </div>
      )}
    </div>
  );
}

function NonDealDetailBody({ kind, detail }) {
  const props = detail.record?.properties || {};
  const [showAll, setShowAll] = useState(false);
  const entries = Object.entries(props).filter(([, v]) => v !== null && v !== "");
  const displayed = showAll ? entries : entries.slice(0, 12);
  return (
    <div className="p-5 space-y-6">
      {["companies", "contacts", "deals"].filter(k => k !== kind).map(other => {
        const list = detail.related?.[other] || []; if (list.length === 0) return null;
        return (
          <div key={other}>
            <h3 className="label-micro mb-2">Related {other}</h3>
            <ul className="divide-y divide-slate-100 border border-slate-200 rounded-sm">
              {list.map(r => (<li key={r.hs_id} className="px-3 py-2 text-sm"><div className="font-semibold text-slate-900">{rowTitle(other, r.properties || {})}</div><div className="text-xs text-slate-500 truncate">{rowSubtitle(other, r.properties || {})}</div></li>))}
            </ul>
          </div>
        );
      })}
      <div>
        <h3 className="label-micro mb-2">All fields ({entries.length})</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-xs">
          {displayed.map(([k, v]) => (<div key={k} className="border-b border-slate-100 pb-1"><div className="text-slate-500 text-[10px] uppercase tracking-wider font-semibold">{k}</div><div className="text-slate-800 break-words">{String(v).slice(0, 300)}</div></div>))}
        </div>
        {entries.length > 12 && <button onClick={() => setShowAll(!showAll)} className="text-xs text-blue-700 hover:underline mt-2" data-testid="crm-show-all-fields">{showAll ? "Show less" : `Show all ${entries.length} fields`}</button>}
      </div>
    </div>
  );
}


// ============================================================================
// Manage Pipelines Modal
// A single admin surface to (a) create native pipelines with seed stages,
// (b) rename / delete pipelines, (c) add / rename / delete stages inside a
// pipeline. Every change hits `/crm/pipelines/*` or `/crm/kanban/columns/*`
// endpoints, so the Kanban board + filters + aggregators light up instantly.
// ============================================================================
function ManagePipelinesModal({ pipelines, onClose, onChanged, onSelectPipeline }) {
  const [tab, setTab] = useState(pipelines[0]?.id || "__new__");
  const [busy, setBusy] = useState(false);
  const [newPipeName, setNewPipeName] = useState("");
  const [newStageName, setNewStageName] = useState("");
  const [newStageColor, setNewStageColor] = useState("slate");
  const [colorPickerFor, setColorPickerFor] = useState(null); // stage id whose picker is open
  const [renameId, setRenameId] = useState(null);
  const [renameVal, setRenameVal] = useState("");
  // Modal maintains its OWN column cache — unscoped (fetches all pipelines'
  // columns) — so switching tabs never shows a stale/empty stage list even
  // when the board is filtered to one pipeline. Refreshed after every CRUD.
  const [allColumns, setAllColumns] = useState([]);

  const reloadColumns = React.useCallback(async () => {
    try {
      const { data } = await api.get("/crm/kanban/columns");
      setAllColumns(data.columns || []);
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => { reloadColumns(); }, [reloadColumns]);

  const pipeStages = useMemo(() => {
    if (!tab || tab === "__new__") return [];
    return (allColumns || [])
      .filter(c => (c.hs_pipeline_id || "default") === tab)
      .sort((a, b) => (a.order || 0) - (b.order || 0));
  }, [tab, allColumns]);

  const activePipe = pipelines.find(p => p.id === tab);

  const createPipeline = async () => {
    const name = newPipeName.trim();
    if (!name) { toast.error("Give the pipeline a name"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/crm/pipelines", { name });
      toast.success(`Pipeline "${name}" created with ${data.stages?.length || 0} default stages`);
      setNewPipeName("");
      await Promise.all([onChanged(), reloadColumns()]);
      setTab(data.id);
      if (onSelectPipeline) onSelectPipeline(data.id);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Create pipeline failed");
    } finally { setBusy(false); }
  };

  // Every mutating action re-fetches the modal's own column cache so
  // switching to a pipeline just-edited shows the fresh state, independent
  // of the board filter.
  const refreshAll = async () => { await Promise.all([onChanged(), reloadColumns()]); };

  const renamePipeline = async (pid, newName) => {
    const name = (newName || "").trim();
    if (!name) return;
    setBusy(true);
    try {
      await api.patch(`/crm/pipelines/${pid}`, { name });
      toast.success("Pipeline renamed");
      setRenameId(null);
      await refreshAll();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Rename failed");
    } finally { setBusy(false); }
  };

  const deletePipeline = async (pipe) => {
    if (pipe.count > 0) {
      toast.error(`Move or delete ${pipe.count} deal(s) first — a pipeline can't be deleted while it has deals.`);
      return;
    }
    if (!window.confirm(`Delete pipeline "${pipe.label}" and all its stages? This can't be undone.`)) return;
    setBusy(true);
    try {
      await api.delete(`/crm/pipelines/${pipe.id}`);
      toast.success("Pipeline deleted");
      await refreshAll();
      setTab(pipelines.find(p => p.id !== pipe.id)?.id || "__new__");
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Delete failed");
    } finally { setBusy(false); }
  };

  const addStage = async () => {
    const name = newStageName.trim();
    if (!name || !tab || tab === "__new__") return;
    setBusy(true);
    try {
      await api.post("/crm/kanban/columns", { name, pipeline_id: tab, color: newStageColor });
      toast.success(`Stage "${name}" added`);
      setNewStageName("");
      setNewStageColor("slate");
      await refreshAll();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Add stage failed");
    } finally { setBusy(false); }
  };

  const changeStageColor = async (col, color) => {
    if (col.color === color) { setColorPickerFor(null); return; }
    setBusy(true);
    try {
      await api.patch(`/crm/kanban/columns/${col.id}`, { color });
      toast.success(`Colour updated`);
      setColorPickerFor(null);
      await refreshAll();
    } catch (e) { toast.error("Colour change failed"); } finally { setBusy(false); }
  };

  const renameStage = async (col) => {
    const name = window.prompt("Rename stage", col.name);
    if (!name || name === col.name) return;
    setBusy(true);
    try {
      await api.patch(`/crm/kanban/columns/${col.id}`, { name: name.trim() });
      toast.success("Stage renamed");
      await refreshAll();
    } catch (e) { toast.error("Rename failed"); } finally { setBusy(false); }
  };

  const deleteStage = async (col) => {
    if (!window.confirm(`Delete stage "${col.name}"?\n\nDeals in this stage will move to "Unassigned".`)) return;
    setBusy(true);
    try {
      await api.delete(`/crm/kanban/columns/${col.id}`);
      toast.success("Stage deleted");
      await refreshAll();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Delete failed");
    } finally { setBusy(false); }
  };

  const moveStage = async (col, direction) => {
    const idx = pipeStages.findIndex(s => s.id === col.id);
    const swapIdx = idx + direction;
    if (swapIdx < 0 || swapIdx >= pipeStages.length) return;
    const a = pipeStages[idx], b = pipeStages[swapIdx];
    setBusy(true);
    try {
      await Promise.all([
        api.patch(`/crm/kanban/columns/${a.id}`, { order: b.order }),
        api.patch(`/crm/kanban/columns/${b.id}`, { order: a.order }),
      ]);
      await refreshAll();
    } catch (e) { toast.error("Reorder failed"); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
         onClick={onClose} data-testid="manage-pipelines-modal">
      <div className="bg-white rounded-sm shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col"
           onClick={(e) => e.stopPropagation()}>
        <header className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Funnel size={18} className="text-blue-700"/>
            <h2 className="font-display font-bold text-slate-900">Manage pipelines & stages</h2>
          </div>
          <button onClick={onClose} data-testid="manage-pipelines-close"
                  className="w-8 h-8 rounded-sm hover:bg-slate-100 grid place-items-center">
            <X size={16}/>
          </button>
        </header>

        <div className="flex flex-1 min-h-0 overflow-hidden">
          {/* Left rail: pipelines list */}
          <aside className="w-64 border-r border-slate-200 bg-slate-50 overflow-y-auto flex-shrink-0">
            <div className="p-3">
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 px-1 mb-1.5">Pipelines</div>
              {pipelines.length === 0 && (
                <div className="text-xs text-slate-500 px-1 py-2 italic">No pipelines yet — create one below.</div>
              )}
              <ul className="space-y-0.5">
                {pipelines.map(p => (
                  <li key={p.id}>
                    <button onClick={() => { setRenameId(null); setTab(p.id); }}
                            data-testid={`manage-pipeline-tab-${p.id}`}
                            className={`w-full text-left rounded-sm px-2.5 py-2 text-xs transition-colors ${tab === p.id ? "bg-blue-600 text-white font-semibold" : "hover:bg-slate-200 text-slate-700"}`}>
                      <div className="flex items-center justify-between gap-2">
                        <span className="truncate">{p.label}</span>
                        <span className={`text-[10px] font-bold px-1.5 rounded-sm ${tab === p.id ? "bg-white/20" : "bg-slate-200"}`}>{p.count}</span>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
              <button onClick={() => { setRenameId(null); setTab("__new__"); }}
                      data-testid="manage-pipeline-new-tab"
                      className={`mt-2 w-full text-left rounded-sm px-2.5 py-2 text-xs border-2 border-dashed transition-colors flex items-center gap-1.5 ${tab === "__new__" ? "border-blue-600 text-blue-700 bg-blue-50 font-semibold" : "border-slate-300 text-slate-600 hover:border-blue-400 hover:text-blue-700"}`}>
                <Plus size={12} weight="bold"/> New pipeline
              </button>
            </div>
          </aside>

          {/* Right pane: details */}
          <div className="flex-1 overflow-y-auto p-6">
            {tab === "__new__" ? (
              <div>
                <h3 className="font-display font-bold text-slate-900 mb-1">Create a new pipeline</h3>
                <p className="text-xs text-slate-500 mb-4">
                  Adds a fresh pipeline with six starter stages (New Lead → Qualified → Proposal Sent → Negotiation → Closed Won → Closed Lost). Rename or delete any of them after creation.
                </p>
                <label className="block">
                  <span className="label-micro">Pipeline name</span>
                  <input value={newPipeName} onChange={(e) => setNewPipeName(e.target.value)}
                         onKeyDown={(e) => e.key === "Enter" && createPipeline()}
                         placeholder="e.g. Enterprise Sales, Partner Referrals…"
                         data-testid="manage-pipeline-new-name"
                         autoFocus
                         className="mt-1 w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"/>
                </label>
                <div className="mt-4 flex gap-2">
                  <button onClick={createPipeline} disabled={busy || !newPipeName.trim()}
                          data-testid="manage-pipeline-create"
                          className="text-sm inline-flex items-center gap-1.5 bg-slate-900 hover:bg-blue-700 disabled:opacity-50 text-white rounded-sm px-4 py-2 transition-colors">
                    <Plus size={13}/>Create pipeline
                  </button>
                </div>
              </div>
            ) : activePipe ? (
              <div>
                <div className="flex items-start justify-between gap-3 mb-4">
                  <div className="min-w-0">
                    {renameId === activePipe.id ? (
                      <div className="flex items-center gap-2">
                        <input value={renameVal} onChange={(e) => setRenameVal(e.target.value)}
                               autoFocus onKeyDown={(e) => e.key === "Enter" && renamePipeline(activePipe.id, renameVal)}
                               data-testid="manage-pipeline-rename-input"
                               className="border border-slate-300 rounded-sm px-2 py-1 text-sm"/>
                        <button onClick={() => renamePipeline(activePipe.id, renameVal)} disabled={busy}
                                className="text-xs bg-slate-900 text-white rounded-sm px-2.5 py-1 hover:bg-blue-700">Save</button>
                        <button onClick={() => setRenameId(null)} className="text-xs border border-slate-300 rounded-sm px-2.5 py-1">Cancel</button>
                      </div>
                    ) : (
                      <>
                        <h3 className="font-display font-bold text-slate-900 truncate">{activePipe.label}</h3>
                        <div className="text-[11px] text-slate-500 mt-0.5">
                          {pipeStages.length} stage{pipeStages.length === 1 ? "" : "s"} · {activePipe.count} deal{activePipe.count === 1 ? "" : "s"}
                          <span className="mx-1.5 text-slate-300">·</span>
                          <code className="text-[10px] bg-slate-100 px-1 py-0.5 rounded">{activePipe.id}</code>
                        </div>
                      </>
                    )}
                  </div>
                  {renameId !== activePipe.id && (
                    <div className="flex items-center gap-1.5 shrink-0">
                      <button onClick={() => { setRenameVal(activePipe.label); setRenameId(activePipe.id); }}
                              data-testid="manage-pipeline-rename-btn"
                              className="text-[11px] inline-flex items-center gap-1 border border-slate-300 rounded-sm px-2.5 py-1.5 hover:bg-slate-100">
                        <PencilSimple size={11}/>Rename
                      </button>
                      <button onClick={() => deletePipeline(activePipe)}
                              data-testid="manage-pipeline-delete-btn"
                              className="text-[11px] inline-flex items-center gap-1 border border-red-300 text-red-700 rounded-sm px-2.5 py-1.5 hover:bg-red-50">
                        <Trash size={11}/>Delete
                      </button>
                    </div>
                  )}
                </div>

                <div className="border border-slate-200 rounded-sm overflow-hidden">
                  <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-wider font-bold text-slate-500">Stages (top → bottom = left → right on the board)</div>
                  {pipeStages.length === 0 ? (
                    <div className="p-6 text-center text-xs text-slate-500 italic">No stages yet — add one below.</div>
                  ) : (
                    <ul className="divide-y divide-slate-100">
                      {pipeStages.map((col, idx) => {
                        const sc = stageColor(col.color);
                        const pickerOpen = colorPickerFor === col.id;
                        return (
                        <li key={col.id} className="px-3 py-2 flex items-center gap-2 hover:bg-slate-50 relative"
                            data-testid={`manage-stage-row-${col.id}`}>
                          <div className="flex flex-col gap-0.5">
                            <button onClick={() => moveStage(col, -1)} disabled={idx === 0 || busy}
                                    title="Move up"
                                    className="w-5 h-4 grid place-items-center rounded-sm hover:bg-slate-200 disabled:opacity-30">
                              <CaretDown size={10} className="rotate-180"/>
                            </button>
                            <button onClick={() => moveStage(col, 1)} disabled={idx === pipeStages.length - 1 || busy}
                                    title="Move down"
                                    className="w-5 h-4 grid place-items-center rounded-sm hover:bg-slate-200 disabled:opacity-30">
                              <CaretDown size={10}/>
                            </button>
                          </div>
                          <span className="text-[10px] font-bold text-slate-400 w-5 text-center tabular-nums">{idx + 1}</span>
                          <button onClick={() => setColorPickerFor(pickerOpen ? null : col.id)} disabled={busy}
                                  data-testid={`manage-stage-color-${col.id}`}
                                  title={`Colour: ${sc.label} · click to change`}
                                  className={`w-6 h-6 rounded-full ${sc.swatch} shrink-0 hover:ring-2 hover:ring-offset-1 hover:ring-slate-700 transition-shadow`}/>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm text-slate-800 truncate">{col.name}</div>
                            <div className="text-[10px] text-slate-500 truncate">
                              <code className="bg-slate-100 px-1 rounded">{col.hs_stage_id}</code>
                            </div>
                          </div>
                          <button onClick={() => renameStage(col)} disabled={busy}
                                  data-testid={`manage-stage-rename-${col.id}`}
                                  className="text-slate-500 hover:text-blue-700 p-1.5 rounded-sm hover:bg-blue-50" title="Rename">
                            <PencilSimple size={12}/>
                          </button>
                          <button onClick={() => deleteStage(col)} disabled={busy}
                                  data-testid={`manage-stage-delete-${col.id}`}
                                  className="text-slate-500 hover:text-red-700 p-1.5 rounded-sm hover:bg-red-50" title="Delete stage">
                            <Trash size={12}/>
                          </button>
                          {pickerOpen && (
                            <div className="absolute z-10 left-12 top-full mt-1 bg-white border border-slate-200 rounded-sm shadow-lg p-2"
                                 data-testid={`manage-stage-color-picker-${col.id}`}>
                              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1.5 px-0.5">Pick colour</div>
                              <div className="grid grid-cols-6 gap-1.5">
                                {STAGE_COLOR_KEYS.map(key => {
                                  const p = STAGE_COLORS[key];
                                  const active = key === col.color;
                                  return (
                                    <button key={key} onClick={() => changeStageColor(col, key)}
                                            title={p.label}
                                            data-testid={`stage-color-swatch-${col.id}-${key}`}
                                            className={`w-6 h-6 rounded-full ${p.swatch} ${active ? "ring-2 ring-offset-1 ring-slate-900" : "hover:ring-2 hover:ring-offset-1 hover:ring-slate-700"} transition-shadow`}/>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </li>
                        );
                      })}
                    </ul>
                  )}
                  <div className="px-3 py-2 bg-slate-50 border-t border-slate-200 flex items-center gap-2 flex-wrap">
                    <input value={newStageName} onChange={(e) => setNewStageName(e.target.value)}
                           onKeyDown={(e) => e.key === "Enter" && addStage()}
                           placeholder="New stage name"
                           data-testid="manage-stage-new-input"
                           className="flex-1 min-w-[160px] border border-slate-300 rounded-sm px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"/>
                    <div className="flex items-center gap-1" data-testid="manage-stage-new-color-picker">
                      {STAGE_COLOR_KEYS.map(key => {
                        const p = STAGE_COLORS[key];
                        const active = key === newStageColor;
                        return (
                          <button key={key} onClick={() => setNewStageColor(key)} title={p.label}
                                  data-testid={`new-stage-color-${key}`}
                                  className={`w-5 h-5 rounded-full ${p.swatch} ${active ? "ring-2 ring-offset-1 ring-slate-900" : "hover:ring-2 hover:ring-offset-1 hover:ring-slate-700"} transition-shadow`}/>
                        );
                      })}
                    </div>
                    <button onClick={addStage} disabled={busy || !newStageName.trim()}
                            data-testid="manage-stage-add-btn"
                            className="text-xs inline-flex items-center gap-1 bg-slate-900 hover:bg-blue-700 disabled:opacity-50 text-white rounded-sm px-3 py-1.5 transition-colors">
                      <Plus size={11}/>Add stage
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-sm text-slate-500 italic">Select a pipeline from the left, or click "New pipeline" to create one.</div>
            )}
          </div>
        </div>

        <footer className="px-6 py-3 border-t border-slate-200 flex justify-end">
          <button onClick={onClose} data-testid="manage-pipelines-done"
                  className="text-sm bg-slate-900 hover:bg-blue-700 text-white rounded-sm px-4 py-2 transition-colors">
            Done
          </button>
        </footer>
      </div>
    </div>
  );
}
